import { z } from "zod";
import {
  FIELD_TYPES,
  SELECTABLE_TYPES,
  PATIENT_MODES,
  MAX_NESTING_DEPTH,
  MAX_HEADING_LENGTH,
  ALPHANUMERIC_WITH_SPACES,
  ALPHANUMERIC_WITH_PARENS,
} from "../domain/clinicalForms/types";

const fieldTypeValues = Object.values(FIELD_TYPES);
const modeValues = PATIENT_MODES.map((m) => m.value);

const optionSchema = z.lazy(() =>
  z.object({
    value: z.string().min(1),
    label: z.string().trim().min(1, "Option label is required"),
    sub_questions: z.array(fieldSchema).optional().default([]),
  })
);

const TABLE_CELL_TYPES = [
  "text",
  "textarea",
  "number",
  "date",
  "datetime",
  "select",
  "multiselect",
  "checkbox",
  "radio",
  "file",
];
const TABLE_CELL_SELECTABLE = ["select", "multiselect", "checkbox", "radio"];

const tableColumnSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().trim().min(1, "Column label is required"),
    type: z.enum(TABLE_CELL_TYPES).default("text"),
    options: z.array(optionSchema).optional(),
  })
  .superRefine((col, ctx) => {
    if (TABLE_CELL_SELECTABLE.includes(col.type)) {
      if (!col.options || col.options.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["options"],
          message: `"${col.label || col.type}" must have at least one option`,
        });
      }
    }
  });

export const fieldSchema = z.lazy(() =>
  z
    .object({
      id: z.string().min(1),
      type: z.enum(fieldTypeValues),
      label: z.string().trim().min(1, "Field label is required"),
      required: z.boolean().optional().default(false),
      placeholder: z.string().optional(),
      description: z.string().optional(),
      min: z.number().optional(),
      max: z.number().optional(),
      step: z.number().optional(),
      options: z.array(optionSchema).optional(),
      columns: z.array(tableColumnSchema).optional(),
      min_rows: z.number().int().nonnegative().optional(),
      max_rows: z.number().int().positive().optional(),
      allow_add_rows: z.boolean().optional(),
      sub_questions: z.array(fieldSchema).optional(),
    })
    .superRefine((field, ctx) => {
      if (field.type === FIELD_TYPES.SECTION_HEADER) {
        if (field.label.trim().length > MAX_HEADING_LENGTH) {
          ctx.addIssue({
            code: "custom",
            path: ["label"],
            message: `Heading must be ${MAX_HEADING_LENGTH} characters or fewer`,
          });
        }
      }
      if (SELECTABLE_TYPES.includes(field.type)) {
        if (!field.options || field.options.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["options"],
            message: `"${field.label || field.type}" must have at least one option`,
          });
        }
      }
      if (field.type === FIELD_TYPES.NUMBER) {
        if (
          field.min !== undefined &&
          field.max !== undefined &&
          field.min > field.max
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["min"],
            message: "Min cannot be greater than max",
          });
        }
      }
      if (field.type === FIELD_TYPES.SLIDER) {
        if (field.min === undefined || field.max === undefined) {
          ctx.addIssue({
            code: "custom",
            path: ["min"],
            message: "Slider requires both min and max",
          });
        } else if (field.min >= field.max) {
          ctx.addIssue({
            code: "custom",
            path: ["min"],
            message: "Min must be less than max",
          });
        }
      }
      if (field.type === FIELD_TYPES.TABLE) {
        if (!field.columns || field.columns.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["columns"],
            message: `"${field.label || field.type}" must have at least one column`,
          });
        }
        if (
          field.min_rows !== undefined &&
          field.max_rows !== undefined &&
          field.min_rows > field.max_rows
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["min_rows"],
            message: "Min rows cannot be greater than max rows",
          });
        }
      }
    })
);

export const sectionSchema = z.object({
  id: z.string().min(1),
  title: z
    .string()
    .trim()
    .min(1, "Section title is required")
    .regex(
      ALPHANUMERIC_WITH_SPACES,
      "Section title can only contain letters and numbers"
    )
    .max(
      MAX_HEADING_LENGTH,
      `Section title must be ${MAX_HEADING_LENGTH} characters or fewer`
    ),
  repeatable: z.boolean().optional().default(false),
  version: z.number().int().positive().optional().default(1),
  fields: z
    .array(fieldSchema)
    .min(1, "Each section must have at least one field"),
});

export const clinicalFormSchema = z.object({
  id: z.string().min(1),
  name: z
    .string()
    .trim()
    .min(1, "Form name is required")
    .regex(
      ALPHANUMERIC_WITH_PARENS,
      "Form name can only contain letters, numbers, and parentheses"
    )
    .max(150, "Form name exceeds the maximum limit"),
  mode: z.enum(modeValues, { message: "Invalid form mode" }),
  active: z.boolean().optional().default(true),
  repeatable: z.boolean().optional().default(false),
  version: z.number().int().positive().optional().default(1),
  sections: z
    .array(sectionSchema)
    .min(1, "Form must have at least one section"),
});

const depthOfField = (field) => {
  const childDepths = [
    ...(field.sub_questions || []).map(depthOfField),
    ...(field.options || []).flatMap((opt) =>
      (opt.sub_questions || []).map(depthOfField)
    ),
  ];
  return 1 + (childDepths.length ? Math.max(...childDepths) : 0);
};

export const validateNestingDepth = (form) => {
  for (const section of form.sections || []) {
    for (const field of section.fields || []) {
      if (depthOfField(field) > MAX_NESTING_DEPTH) {
        return `Nesting depth exceeds the limit of ${MAX_NESTING_DEPTH}`;
      }
    }
  }
  return null;
};

export const firstIssueMessage = (zodError) =>
  zodError?.issues?.[0]?.message || "Form is invalid";

export const formatSaveError = (zodError, form) => {
  const issue = zodError?.issues?.[0];
  if (!issue) return "Form is invalid";

  if (issue.path[0] === "sections" && typeof issue.path[1] === "number") {
    const section = form?.sections?.[issue.path[1]];
    const sectionName = section?.title?.trim() || `Section ${issue.path[1] + 1}`;

    if (issue.path[2] === "fields") {
      return `"${sectionName}" must have at least one field`;
    }

    return `"${sectionName}": ${issue.message}`;
  }

  return issue.message;
};
